import { DataSetType } from 'libs/end-to-end/src/types/databases/data-sets/data-sets';
import { IntegrationType } from 'libs/end-to-end/src/types/databases/integrations/integration-type';
import { SupplementalDatasetType } from 'libs/end-to-end/src/datasets';
import { DataSetConfig } from 'libs/end-to-end/src/types/databases/data-sets/data-set';
import { createDefaultConfigForHttpIntegration } from 'libs/end-to-end/src/types/databases/data-sets/create-default-config-for-http-integration';
import { createDefaultConfigForMergeIntegration } from 'libs/end-to-end/src/types/databases/data-sets/create-default-config-for-merge-integration';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { MergeCategoryEnum } from 'libs/end-to-end/src/merge';
import { createDefaultConfigForPostgresIntegration } from 'libs/end-to-end/src/types/databases/data-sets/create-default-config-for-postgres-integration';
import { ResumeQualityScoreRubric } from 'libs/end-to-end/src/types/resume-parser/resume-quality-score-rubric';

export const createDefaultDataSetConfig = ({
  type,
  integrationType,
  supplementalDataSetType,
  category,

  resumeQualityScoreRubrics,
}: {
  type: DataSetType;
  integrationType: IntegrationType | null;
  supplementalDataSetType: SupplementalDatasetType | null;
  category?: MergeCategoryEnum | null;

  resumeQualityScoreRubrics?: ResumeQualityScoreRubric[];
}): DataSetConfig => {
  if (type === 'Integration') {
    if (integrationType === null) {
      throw new Error('integrationType is required for Integration data sets');
    }

    if (integrationType === 'HTTP') {
      return createDefaultConfigForHttpIntegration();
    }

    if (integrationType === 'Postgres') {
      return createDefaultConfigForPostgresIntegration();
    }

    if (integrationType === 'Merge') {
      return createDefaultConfigForMergeIntegration({
        category: category ?? 'hris',
      });
    }

    if (integrationType === 'Snowflake') {
      return {
        dataSetType: type,
        integration: {
          integrationType,
          organizationId: '',
          dbtAction: '',
          dataSetId: '',
          databaseId: '',
          versionId: '',
        },
      };
    }

    if (integrationType === 'ADP') {
      return {
        dataSetType: type,
        integration: {
          integrationType,
          clientIdSecretReference: { secretId: '' },
          clientSecretSecretReference: { secretId: '' },
          sslCertSecretReference: { secretId: '' },
          keyCertSecretReference: { secretId: '' },
        },
      };
    }

    if (integrationType === 'SqlServer') {
      return {
        dataSetType: type,
        integration: {
          integrationType,
          host: '',
          port: 1433,
          tables: [],
          user: undefined,
          password: undefined,
        },
      };
    }

    return {
      dataSetType: type,
      integration: {
        integrationType,
      },
    };
  }

  if (type === 'File') {
    return {
      dataSetType: type,
      fileName: '',
    };
  }

  if (type === 'Supplemental') {
    if (supplementalDataSetType === null) {
      throw new Error(
        'supplementalDataSetType is required for Supplemental data sets',
      );
    }

    if (supplementalDataSetType === 'resumes') {
      return {
        dataSetType: type,
        supplementalDataSetType,
        openAiModel: 'gpt-4o-mini',
        enableCompositeResumeScoring: false,
        useV2Grading: false,
        rubricsByJobTitle:
          resumeQualityScoreRubrics?.map((r) => ({
            filter: { type: 'equals', value: r.score_key },
            rubricConstructId: r.score_key,
          })) ?? [],
      };
    }

    return {
      dataSetType: type,
      supplementalDataSetType,
    };
  }

  if (type === 'Composite') {
    return {
      dataSetType: type,
      taggedDataSetReferences: [],
      pythonScriptContents: '',
      recipe: null,
      runOrchestrationOnBatch: true,
      runScriptOnBatch: true,
    };
  }

  throw guardExhaustive(type);
};
